import { useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, AuthCallback } from "@/auth";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { WhatsAppFloat } from "@/components/WhatsAppFloat";
import { MobileCtaBar } from "@/components/MobileCtaBar";
import HomePage from "@/pages/HomePage";
import CoursesPage from "@/pages/CoursesPage";
import CourseDetailPage from "@/pages/CourseDetailPage";
import PolicyPage from "@/pages/PolicyPage";
import DashboardPage from "@/pages/DashboardPage";

const useLenis = () => {
  const lenisRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    lenisRef.current = lenis;
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    const onClick = (e) => {
      const a = e.target.closest('a[href^="/#"], a[href^="#"]');
      if (!a) {
        const nav2 = e.target.closest('a[href^="/"]');
        if (nav2 && !nav2.target) {
          e.preventDefault();
          navigate(nav2.getAttribute("href"));
        }
        return;
      }
      e.preventDefault();
      const hash = a.getAttribute("href").split("#")[1];
      const scrollToHash = () => {
        const el = document.getElementById(hash);
        if (el) lenis.scrollTo(el, { offset: -72, duration: 1.4 });
      };
      if (a.getAttribute("href").startsWith("/#") && window.location.pathname !== "/") {
        navigate("/");
        setTimeout(scrollToHash, 250);
      } else {
        scrollToHash();
      }
    };
    document.addEventListener("click", onClick);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      document.removeEventListener("click", onClick);
    };
  }, [navigate]);

  useEffect(() => {
    if (!location.hash && lenisRef.current) {
      lenisRef.current.scrollTo(0, { immediate: true });
    }
  }, [location.pathname, location.hash]);
};

const AppRoutes = () => {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/courses" element={<CoursesPage />} />
      <Route path="/courses/:slug" element={<CourseDetailPage />} />
      <Route path="/dashboard" element={<DashboardPage />} />
      <Route path="/policies/:policyKey" element={<PolicyPage />} />
      <Route path="*" element={<HomePage />} />
    </Routes>
  );
};

const Shell = () => {
  useLenis();
  return (
    <div className="min-h-screen bg-[#060913]">
      <Header />
      <main>
        <AppRoutes />
      </main>
      <Footer />
      <div className="h-[68px] lg:hidden" aria-hidden="true" />
      <WhatsAppFloat />
      <MobileCtaBar />
      <Toaster position="top-center" richColors theme="dark" />
    </div>
  );
};

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AuthProvider>
          <Shell />
        </AuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
